import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowRight, Lock } from "lucide-react";
import { INTEGRATION_API, startYouCanOAuth } from "../../api/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { FeedbackMessage, getApiErrorMessage } from "../ui/Feedback";
import shopifyLogo from "../../platforms logo/shopify.png";
import youcanLogo from "../../platforms logo/youcan.png";
import sallaLogo from "../../platforms logo/salla.png";
import woocommerceLogo from "../../platforms logo/woocommerce.png";
import zidLogo from "../../platforms logo/zid.png";
import dropifyLogo from "../../platforms logo/dropify.png";
import lightfunnelsLogo from "../../platforms logo/lightfunnels.png";
const IntegrationAdd = () => {
  const [search, setSearch] = useState("");
  const [selectedIntegration, setSelectedIntegration] = useState(null);
const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [shopifyForm, setShopifyForm] = useState({
    store_name: "",
    external_store_id: "",
    client_id_platform: "",
    client_secret_platform: "",
  });

  const navigate = useNavigate();
  const location = useLocation();

  /* ================= HANDLE CALLBACK ================= */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const platform = params.get("platform");
    const message = params.get("message");

    if (!status) return;

    if (status === "success") {
      setSuccess(`${platform} connected successfully`);
      setSelectedIntegration(null);
    }

    if (status === "error") {
      setError(message || "Connection failed");
    }

    navigate("/integrations/add", { replace: true });
  }, [location.search, navigate]);

  /* ================= STATIC CARDS LIST ================= */
  const PLATFORMS = useMemo(() => {
    return [
      {
        id: "shopify",
        name: "Shopify",
        subtitle: "Gratuit à installer",
        description:
          "Create your team to confirm and ship orders to delivery companies with just one click.",
        logo: shopifyLogo,
        enabled: true,
      },
      {
        id: "youcan",
        name: "YouCan",
        subtitle: "Gratuit à installer",
        description:
          "Connect your YouCan store and sync orders automatically for faster processing.",
        logo: youcanLogo,
        enabled: true,
      },

      // Coming soon
      {
        id: "salla",
        name: "Salla",
        subtitle: "Coming soon",
        description: "Salla integration is under development. It will be available soon.",
        logo: sallaLogo,
        enabled: false,
      },
      {
        id: "woocommerce",
        name: "WooCommerce",
        subtitle: "Coming soon",
        description: "WooCommerce integration is under development. It will be available soon.",
        logo: woocommerceLogo,
        enabled: false,
      },
      {
        id: "zid",
        name: "Zid",
        subtitle: "Coming soon",
        description: "Zid integration is under development. It will be available soon.",
        logo: zidLogo,
        enabled: false,
      },
      {
        id: "dropify",
        name: "Dropify",
        subtitle: "Coming soon",
        description: "Dropify integration is under development. It will be available soon.",
        logo: dropifyLogo,
        enabled: false,
      },
      {
        id: "lightfunnels",
        name: "Lightfunnels",
        subtitle: "Coming soon",
        description: "Lightfunnels integration is under development. It will be available soon.",
        logo: lightfunnelsLogo,
        enabled: false,
      },
    ];
  }, []);

  /* ================= FILTER ================= */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PLATFORMS;
    return PLATFORMS.filter((p) => p.name.toLowerCase().includes(q));
  }, [PLATFORMS, search]);

  /* ================= CONNECT ================= */
  const handleConnect = async (platform) => {
    setError("");
    setSuccess("");

    if (!platform.enabled) return;

    // ✅ Shopify -> modal
    if (platform.id === "shopify") {
      setSelectedIntegration(platform);
      return;
    }

    // ✅ YouCan -> use backend config (same as your old code)
    if (platform.id === "youcan") {
      try {
        setLoading(true);
        const response = await startYouCanOAuth();
        window.location.assign(response.data.authorization_url);
      } catch (requestError) {
        setError(getApiErrorMessage(requestError, "Impossible de démarrer la connexion YouCan."));
        setLoading(false);
      }
      return;
    }
  };

  /* ================= SHOPIFY SUBMIT ================= */
  const handleShopifySubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await INTEGRATION_API.post(
        "integrations/stores/shopify/manual/connect/",
        { platform: "shopify", ...shopifyForm }
      );

      window.location.assign(res.data.authorization_url);
    } catch (err) {
      setError(getApiErrorMessage(err, "Shopify connection failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-8 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Add Integration</h1>
          <p className="text-gray-500 mt-1 dark:text-slate-500">Install integrations like an App Store.</p>
        </div>

        {/* Alerts */}
        {success && (
          <div className="mb-4">
            <FeedbackMessage type="success" title="Integration connectee" message={success} onClose={() => setSuccess("")} />
          </div>
        )}
        {error && (
          <div className="mb-4">
            <FeedbackMessage type="error" title="Action impossible" message={error} onClose={() => setError("")} />
          </div>
        )}

        {/* Search */}
        <div className="relative mb-8 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={18} />
          <input
            type="text"
            placeholder="Search integrations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
          />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <motion.button
              key={p.id}
              type="button"
              onClick={() => handleConnect(p)}
              whileHover={p.enabled ? { y: -4 } : {}}
              whileTap={p.enabled ? { scale: 0.99 } : {}}
              disabled={!p.enabled}
              className={[
                "text-left group w-full rounded-2xl p-6 border shadow-sm transition",
                p.enabled
                  ? "bg-white border-gray-200 hover:shadow-md cursor-pointer dark:bg-slate-900 dark:border-slate-800"
                  : "bg-white/70 border-gray-200 cursor-not-allowed opacity-75 dark:bg-slate-900/70 dark:border-slate-800",
              ].join(" ")}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="h-14 w-14 rounded-2xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden shrink-0 dark:bg-slate-900 dark:border-slate-800">
                  <img src={p.logo} alt={p.name} className="h-10 w-10 object-contain" />
                </div>

                {/* Text */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate dark:text-slate-100">
                        {p.name}
                      </h3>
                      <p className="text-sm mt-0.5 text-gray-500 inline-flex items-center dark:text-slate-500">
                        {!p.enabled && <Lock size={14} className="mr-1.5" />}
                        {p.subtitle}
                      </p>
                    </div>

                    <div
                      className={[
                        "transition-opacity",
                        p.enabled ? "opacity-0 group-hover:opacity-100 text-gray-400 dark:text-slate-500" : "opacity-0",
                      ].join(" ")}
                    >
                      <ArrowRight size={18} />
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mt-4 leading-relaxed line-clamp-3 dark:text-slate-400">
                    {p.description}
                  </p>

                  <div className="mt-5 flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-600 bg-gray-50 dark:border-slate-700 dark:text-slate-400 dark:bg-slate-800">
                      {p.enabled ? "Available" : "Coming soon"}
                    </span>
                    {(p.id === "shopify" || p.id === "youcan") && (
                      <span className="inline-flex items-center rounded-full border border-indigo-200 px-2.5 py-1 text-xs text-indigo-700 bg-indigo-50 dark:border-indigo-500/30 dark:text-indigo-400 dark:bg-indigo-500/10">
                        OAuth
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* ================= SHOPIFY MODAL ================= */}
        <AnimatePresence>
          {selectedIntegration?.id === "shopify" && (
            <motion.div
              className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                className="bg-white w-full max-w-md rounded-2xl shadow-xl p-7 relative dark:bg-slate-900"
              >
                <button
                  onClick={() => setSelectedIntegration(null)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
                >
                  <X size={18} />
                </button>

                <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">Connect Shopify Store</h2>
                <p className="text-sm text-gray-500 mt-1 mb-6 dark:text-slate-500">
                  Enter your store details then continue to Shopify authorization.
                </p>

                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Store Name"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                    onChange={(e) =>
                      setShopifyForm({ ...shopifyForm, store_name: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Shop Domain (myshop.myshopify.com)"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                    onChange={(e) =>
                      setShopifyForm({ ...shopifyForm, external_store_id: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Shopify API Key"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                    onChange={(e) =>
                      setShopifyForm({ ...shopifyForm, client_id_platform: e.target.value })
                    }
                  />
                  <input
                    type="password"
                    placeholder="Shopify App Secret"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                    onChange={(e) =>
                      setShopifyForm({
                        ...shopifyForm,
                        client_secret_platform: e.target.value,
                      })
                    }
                  />
                </div>

<button
  onClick={handleShopifySubmit}
  disabled={loading}
  className="mt-6 flex w-full items-center justify-center rounded-xl bg-indigo-600 py-3 font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
>
  {loading ? (
    <div className="flex items-center gap-2">
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
      <span>Connecting...</span>
    </div>
  ) : (
    "Continue to Shopify"
  )}
</button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default IntegrationAdd;
